import { hasBannerflowHostedVideo } from '@creative/elements/utils';
import { imageOptimizerDefaults, ImageOptimizerUrlBuilder } from '@creative/image-optimizer';
import { clearDiContainers, diInject, diInjectable, registerDependency } from '@di/di';
import { AdEvents } from '@domain/ad/ad-events';
import { CreativeMode } from '@domain/creative/environment';
import { addColorSchemeMetaTag, emptyIframeSrc } from '@studio/utils/ad/dom';
import { FontLoader, injectFontStyle } from '@studio/utils/font-loading';
import { _performanceMark } from '@studio/utils/performance';
import { SafeEval } from '@studio/utils/safe-eval';
import { concatUrl, getDomain, isBannerflow, isRelativeUrl, isUrl, parameterToBool, replaceOrigin } from '@studio/utils/url';
import { AnimatedCreativeAdApi } from './animated-creative-ad-api';
import { registerFeaturesByFiles } from './animated-creative.dependencies';
import { getCustomFPSInterval } from './animation.utils';
import { Animator } from './animator';
import { hasFeededContent, isFeedValue } from './elements/feed/feeds.utils';
import { createRichTextFromString, isVariableSpan } from './elements/rich-text/utils';
import { FONT_SERVICE_TEXT_PARAMETER, getFontServiceParamsFromString } from './font-service.utils';
import { CreativeDataNode, isDynamicPropertyElement, isImageNode, isTextNode, isVideoNode, isWidgetNode } from './nodes';
import { Renderer } from './renderer';
import { getParsedCreativeData, mapAssets } from './serialization/element-parser';
export class AnimatedCreative {
    get _window() {
        var _a;
        return (this._inSandboxedIframe ? window : (_a = this._iframe) === null || _a === void 0 ? void 0 : _a.contentWindow) || undefined;
    }
    get _document() {
        var _a;
        const document = (_a = this._window) === null || _a === void 0 ? void 0 : _a.document;
        if (!document) {
            throw new Error('No document found');
        }
        return document;
    }
    constructor(_ad, _creativeData, _preloadAssets, env, _onLoadCallback) {
        this._ad = _ad;
        this._creativeData = _creativeData;
        this._preloadAssets = _preloadAssets;
        this._onLoadCallback = _onLoadCallback;
        this._preloadedFeeds = new Set();
        this._inSandboxedIframe = false;
        this._destroyed = false;
        /**
         * When iframe window is ready to use, init creative
         */
        this._init = async () => {
            var _a;
            console.debug('[AnimatedCreative] init');
            _performanceMark('ac:init:start');
            const contentWindow = this._window;
            if (!contentWindow) {
                throw new Error('Ad container is not visible');
            }
            if (this._destroyed) {
                return;
            }
            FontLoader.setBrowserDocument(this._document);
            FontLoader.setEnvs(this._env);
            this._insertCreativeStyles();
            this._rootElement = this._getRootElement();
            registerDependency(0 /* Token.AD */, this._ad);
            diInjectable(1 /* Token.RENDERER */, Renderer).withArgs(this._creativeDataNode, {
                contentWindow,
                fps: this._ad.parameters.fps,
                responsive: parameterToBool(this._ad.parameters.responsive)
                    ? {
                        enabled: true,
                        mode: this._ad.parameters['responsive-mode']
                    }
                    : undefined,
                // Preloaded in _preloadFonts
                fontFamilies: []
            }, this._env);
            diInjectable(3 /* Token.ANIMATOR */, Animator).withArgs(this._env);
            console.debug('[AnimatedCreative] loading feature files / init IO / SafeEval');
            _performanceMark('ac:init:load:features:start');
            await Promise.all([
                registerFeaturesByFiles(this._ad.selectedCreative.animated.files, this._ad.getOrigin(), this._env, this._ad.data.brand.id),
                ImageOptimizerUrlBuilder.init_m(((_a = this._env) === null || _a === void 0 ? void 0 : _a.IMAGE_OPTIMIZER_ORIGIN) || '', this._ad.selectedCreative.animated.defaultOrigin),
                SafeEval.init(this._inSandboxedIframe)
            ]);
            console.debug('[AnimatedCreative] loaded feature files / init IO / SafeEval');
            _performanceMark('ac:init:load:features:end');
            this._creativeDataNode.startTime = this._getStartTime();
            this.renderer = diInject(1 /* Token.RENDERER */);
            this.feedStore = diInject(4 /* Token.FEED_STORE */, { optional: true });
            this.renderer.on('click', this._onClick);
            if (this.feedStore) {
                _performanceMark('ac:init:load:feeds:start');
                await this._preloadFeeds();
                _performanceMark('ac:init:load:feeds:end');
            }
            this.animator = diInject(3 /* Token.ANIMATOR */);
            this._initEventBubbling();
            this._registerAdApi();
            _performanceMark('ac:init:render:start');
            // Viewtree has to be initialized before preloading fonts, images and videos
            this.renderer.initialize(this._rootElement, this._creativeDataNode.startTime, true);
            _performanceMark('ac:init:render:end');
            try {
                console.debug('[AnimatedCreative] preloading fonts');
                await this._preloadFonts();
                console.debug('[AnimatedCreative] loaded fonts');
            }
            catch (e) {
                console.error('Failed to preload fonts', e);
            }
            finally {
                // Note: We have to rerender to include the fonts
                // TODO: Remove this hack in the future. when we rewrite renderer to have loadAssets method.
                _performanceMark('ac:init:rerender:start');
                this.renderer.rerender_m(this._creativeDataNode);
                _performanceMark('ac:init:rerender:end');
            }
            try {
                console.debug('[AnimatedCreative] preloading elements');
                _performanceMark('ac:init:load:elements:start');
                const preloadElements = [...this.renderer.preloadingElements.values()];
                // not supported in all browsers
                if (Promise.allSettled) {
                    const results = await Promise.allSettled(preloadElements);
                    console.debug('[AnimatedCreative] loaded elements');
                    results.forEach(result => {
                        if (result.status === 'rejected') {
                            throw result.reason;
                        }
                    });
                }
                else {
                    await Promise.all(preloadElements);
                }
                _performanceMark('ac:init:load:elements:end');
            }
            catch (err) {
                if (!err) {
                    console.error('`undefined` error occurred while preloading elements');
                    return;
                }
                const error = err;
                if (error.message) {
                    this._handleError(error.message, {
                        image: error.url ? { url: error.url } : undefined,
                        originalError: err
                    });
                }
                else {
                    console.error(err);
                }
            }
            this._checkForWidgetElementNodes();
            if (this.renderer.WidgetRenderer) {
                try {
                    console.debug('[AnimatedCreative] loading widgets');
                    _performanceMark('ac:init:load:widgets:start');
                    // Ugly workaround to ensure that all widgets has completely loaded to ensure that they are in sync with the creative
                    await Promise.all(this.renderer.WidgetRenderer.widgetInitializingPromises);
                    await Promise.all(this.renderer.WidgetRenderer.widgetLoadingPromises);
                }
                catch (err) {
                    let message = 'Unknown error.';
                    let contexts;
                    if (err instanceof Error) {
                        message = err.message;
                        contexts = err.contexts;
                    }
                    if (typeof err === 'string') {
                        message = err;
                    }
                    this._handleError(message, contexts);
                }
                finally {
                    console.debug('[AnimatedCreative] loaded widgets');
                    _performanceMark('ac:init:load:widgets:end');
                }
            }
            // Sometimes iframe is hidden (display: none), for on-site banners. Where the publisher have
            // implemented media queries to show and hide multiple banners to simulate responsiveness.
            // Hidden loaded banners have the fallback, that they have zero height when inspected, which
            // currently affects text rendering. So, we have to wait until it is shown to rerender.
            _performanceMark('ac:init:visibility:check');
            if (!this._isPixelVisible()) {
                console.debug('[AnimatedCreative] not visible. Adding visibility listener');
                this._pixelVisibleInterval = contentWindow.setInterval(() => {
                    var _a;
                    if (this._isPixelVisible()) {
                        console.debug('[AnimatedCreative] became visible');
                        (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.rerender_m(this._creativeDataNode);
                        this._showIframe();
                        if (Animator) {
                            this._initPlayback();
                        }
                        contentWindow.clearInterval(this._pixelVisibleInterval);
                    }
                }, 10);
            }
            else {
                this._showIframe();
                if (Animator) {
                    this._initPlayback();
                }
            }
            if (typeof this._onLoadCallback === 'function') {
                this._onLoadCallback(this);
            }
            _performanceMark('ac:init:end');
        };
        /**
         * When user clicks the creative
         */
        this._onClick = (clickEvent) => {
            let event;
            let deepLinkUrl = '';
            if (!(clickEvent instanceof MouseEvent)) {
                event = clickEvent.event;
                deepLinkUrl = clickEvent.deepLinkUrl;
            }
            else {
                event = clickEvent;
            }
            const url = this._getClickUrl();
            if (!url) {
                return;
            }
            if (deepLinkUrl) {
                this._ad.tracking.click({ x: event.x, y: event.y, deepLinkUrl });
            }
            else {
                this._ad.tracking.click(event);
            }
        };
        console.debug('[AnimatedCreative] construct');
        try {
            const data = this._prepareData(_creativeData);
            this._creativeDataNode = new CreativeDataNode(data);
        }
        catch (e) {
            throw new Error(`Could not prepare creative data: ${e === null || e === void 0 ? void 0 : e.message}`);
        }
        try {
            this._setEnvironments(Object.assign(Object.assign({}, env), { MODE: CreativeMode.AnimatedCreative }));
            const iframe = this._createIframe();
            console.debug('[AnimatedCreative] created iframe');
            const contentWindow = iframe.contentWindow;
            this._inSandboxedIframe =
                !iframe.contentDocument || !!parameterToBool(this._ad.parameters['iframeless']);
            if (this._inSandboxedIframe) {
                console.debug('[AnimatedCreative] is sandboxed. Removing iframe');
                iframe.remove();
                this._init();
            }
            else if (contentWindow) {
                addColorSchemeMetaTag(iframe);
                this._iframe = iframe;
                // Wait for iframeWindow to be available
                this._onIframeWindowLoad(contentWindow, this._init);
            }
            else {
                console.warn('Did not initialize the creative, due to container element is not visible:\n', this._ad.container);
                return;
            }
        }
        catch (e) {
            throw new Error(`Could not initialize creative: ${e === null || e === void 0 ? void 0 : e.message}`);
        }
    }
    getTrackerPlaythroughData() {
        if (!this.animator) {
            throw new Error('Animator is not initialized');
        }
        const trackingElements = this._creativeDataNode.elements.filter(el => isVideoNode(el) || isWidgetNode(el));
        const videoStorage = this._env.VIDEOS_STORAGE_ORIGIN;
        const playTime = this.animator.time + this.animator.duration * (this.animator.loop - 1);
        return {
            loop: this.animator.loop,
            playTime,
            progress: Math.min(playTime / this.animator.duration, 1),
            isBannerflowHosted: Number(trackingElements.some(el => hasBannerflowHostedVideo(el, videoStorage))),
            elements: [
                ...trackingElements.reduce((tags, el) => {
                    let tag;
                    if (isWidgetNode(el)) {
                        tag = 'w';
                    }
                    else if (isVideoNode(el)) {
                        tag = 'v';
                    }
                    else if (isImageNode(el)) {
                        tag = 'i';
                    }
                    else if (isTextNode(el)) {
                        tag = 't';
                    }
                    if (tag) {
                        if (hasFeededContent(el)) {
                            tags.add(`${tag}f`);
                        }
                        else {
                            tags.add(tag);
                        }
                    }
                    return tags;
                }, new Set())
            ]
        };
    }
    _getPreloadFonts() {
        return this._preloadAssets.filter(asset => asset.type === 'font');
    }
    async updateDynamicPropertyElements_m(dynamicProperties) {
        await Promise.all(dynamicProperties.map(dynamicProperty => this._updateDynamicPropertyElement(dynamicProperty)));
    }
    async _updateDynamicPropertyElement(dynamicProperty) {
        var _a, _b, _c, _d, _e;
        const creativeElement = this._creativeData.elements.find(element => isDynamicPropertyElement(element) && element.dynamicPropertyId === dynamicProperty.id);
        if (!creativeElement) {
            throw new Error(`Element with dynamic property ID '${dynamicProperty.id}' not found.`);
        }
        const node = this._creativeDataNode.findNodeById_m(creativeElement.id);
        if (!node) {
            throw new Error(`Node with ID '${creativeElement.id}' not found.`);
        }
        const dynamicPropertyValue = 'value' in dynamicProperty && dynamicProperty.value;
        if (isTextNode(node) && isTextNode(creativeElement)) {
            if (typeof dynamicPropertyValue === 'string') {
                const newContent = dynamicPropertyValue;
                const newText = createRichTextFromString(newContent);
                node.content.spans = newText.spans;
                if (node.font) {
                    await this._preloadFont(node.font, newContent);
                }
            }
            else {
                node.content = creativeElement.content;
            }
        }
        else if (isImageNode(node) && isImageNode(creativeElement)) {
            if (!node.imageAsset) {
                throw new Error(`Image node with ID '${node.id}' has no image asset.`);
            }
            if (typeof dynamicPropertyValue === 'string') {
                node.imageAsset.url = dynamicPropertyValue;
            }
            else {
                node.imageAsset.url = (_b = (_a = creativeElement.imageAsset) === null || _a === void 0 ? void 0 : _a.url) !== null && _b !== void 0 ? _b : '';
            }
        }
        (_c = this.renderer) === null || _c === void 0 ? void 0 : _c.preloadingElements.delete(creativeElement.id);
        (_d = this.renderer) === null || _d === void 0 ? void 0 : _d.rerender_m(this._creativeDataNode);
        return (_e = this.renderer) === null || _e === void 0 ? void 0 : _e.preloadingElements.get(creativeElement.id);
    }
    _getRootElement() {
        if (this._inSandboxedIframe) {
            return this._ad.container;
        }
        else {
            return this._document.body;
        }
    }
    /**
     * @summary
     * Check pixel visibilty.
     *
     * @description
     * The ability to render visible pixels on the banner. Notice, this API is different from
     * DOM visibility, where display none is still counted as visibly. We have manually confirmed
     * that display none:ed DOM subtree's are cheap to check bounding rect.
     */
    _isPixelVisible() {
        const testElement = this._document.createElement('div');
        const style = testElement.style;
        style.height = '1px';
        style.width = '1px';
        style.position = 'absolute';
        style.top = '-100000px';
        this._ad.container.appendChild(testElement);
        const result = testElement.getBoundingClientRect().height > 0;
        this._ad.container.removeChild(testElement);
        return result;
    }
    _checkForWidgetElementNodes() {
        if (!this.renderer) {
            return;
        }
        for (const element of this.renderer.creativeDocument.elements) {
            if (isWidgetNode(element)) {
                this.hasWidgetElements = true;
                break;
            }
        }
    }
    _initPlayback() {
        _performanceMark('ac:animator:start');
        if (!this.animator) {
            return;
        }
        const autoplay = this._ad.shouldAutoplay();
        const fpsInterval = getCustomFPSInterval(this._ad.parameters.fps);
        if (fpsInterval) {
            this.animator.updateCustomFPSInterval(fpsInterval);
        }
        if (this._env.MODE === CreativeMode.VideoGenerator) {
            this._creativeDataNode.loops = 1;
        }
        // Play/pause creative when it leaves or enter view
        if (this._ad.viewability.pauseOutsideView()) {
            this._addOutOfViewEvents(autoplay);
        }
        else if (autoplay) {
            console.debug('[AnimatedCreative] autoplaying');
            this.animator.play();
        }
        this._ad.on(AdEvents.Stop, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.pause();
        });
        _performanceMark('ac:animator:end');
    }
    _addOutOfViewEvents(autoplay) {
        var _a;
        this._ad.on(AdEvents.ViewEnter, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.play();
        });
        this._ad.on(AdEvents.ViewLeave, () => {
            var _a;
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.pause();
        });
        if (this._ad.viewability.visible && autoplay) {
            (_a = this.animator) === null || _a === void 0 ? void 0 : _a.play();
        }
    }
    _getStartTime() {
        var _a;
        const seek = parseFloat(((_a = this._ad.parameters) === null || _a === void 0 ? void 0 : _a.seek) || '');
        // Seek parameter is added to the tag
        if (!isNaN(seek)) {
            // VRS sets "seek" to -1
            return Math.max(0, seek);
        }
        // Used by image generator to capture preload image at the right place in timeline
        if (this._shouldStartFromPreloadFrame()) {
            const preloadFrame = this._creativeDataNode.preloadImage.frames[0];
            // Defaults to 1 if not set
            return typeof preloadFrame === 'number' ? preloadFrame : 1;
        }
        else {
            return this._creativeDataNode.startTime || 0;
        }
    }
    _shouldStartFromPreloadFrame() {
        var _a;
        return ((_a = this._ad.parameters) === null || _a === void 0 ? void 0 : _a.seek) === 'preload-frame';
    }
    _setEnvironments(env) {
        if (env) {
            this._env = env;
            if (this._ad.parameters.env === 'image-generator' || this._shouldStartFromPreloadFrame()) {
                this._env.MODE = CreativeMode.ImageGenerator;
            }
            else if (this._ad.parameters.env === 'video-generator') {
                this._env.MODE = CreativeMode.VideoGenerator;
            }
            if (!isBannerflow(window.location.href)) {
                const origin = this._ad.getOrigin();
                if (!isBannerflow(origin)) {
                    this._env.CUSTOM_AD_DOMAIN = getDomain(origin, false);
                }
                if (origin) {
                    // if the ad is served from a custom domain, we need to fix all origins
                    for (const e in this._env) {
                        const value = this._env[e];
                        if (!isUrl(value)) {
                            continue;
                        }
                        if (isRelativeUrl(value)) {
                            this._env[e] = concatUrl(origin, value);
                        }
                        else if (!origin.includes('bannerflow.com/preview')) {
                            // don't replace origin for ACG hosted ads in production
                            this._env[e] = replaceOrigin(value, origin);
                        }
                    }
                }
            }
            imageOptimizerDefaults.imageOptimizerOrigin = this._env.IMAGE_OPTIMIZER_ORIGIN;
        }
    }
    /**
     * Create wrapping iframe.
     * Note that in this stage we cannot insert content into the iframe in all browers (FF)
     */
    _createIframe() {
        const iframe = document.createElement('iframe');
        // Dimension hacks to fix iframe issues in iOS:
        // https://stackoverflow.com/a/20142280
        const style = iframe.style;
        style.width = style.height = '1px';
        style.minWidth = style.maxWidth = style.minHeight = '100%';
        style.outline = style.border = style.transform = style.filter = 'none';
        style.display = 'block';
        style.position = 'absolute';
        style.left = style.top = style.opacity = '0';
        style.transformOrigin = '0 0';
        iframe.src = this._ad.selectedCreative.id === '7705683' ? 'about:blank' : emptyIframeSrc();
        iframe.scrolling = 'no';
        if (this._ad.shouldFadeIn()) {
            style.transition = 'opacity 0.2s ease';
        }
        this._ad.container.appendChild(iframe);
        return iframe;
    }
    _showIframe() {
        console.debug('[AnimatedCreative] showing iframe');
        _performanceMark('ac:iframe:show');
        if (this._iframe) {
            this._iframe.style.opacity = '1';
        }
    }
    /**
     * Needs to be executed AFTER the document is ready
     */
    _insertCreativeStyles() {
        const url = this._getClickUrl();
        const cursor = url ? 'pointer;' : 'default;';
        const doc = this._document;
        const style = doc.createElement('style');
        style.innerHTML = `*{box-sizing: border-box;}:root{color-scheme: light only;}html,body{margin:0;padding:0;overflow:hidden;width:100%;height:100%;-ms-user-select:none;-moz-user-select:none;-webkit-user-select:none;user-select:none;cursor:${cursor};}`;
        this._creativeStylesRef = style;
        doc.head.appendChild(style);
    }
    _getClickUrl() {
        const redirectUrl = this._ad.tracking.getRedirectUrl();
        if (redirectUrl === false) {
            return;
        }
        return redirectUrl || this._ad.tracking.getTargetUrl();
    }
    /**
     * When iframe window is ready
     * @param callback
     */
    _onIframeWindowLoad(iframeWindow, callback) {
        console.debug('[AnimatedCreative] waiting for iframe');
        if (iframeWindow.document.readyState === 'complete') {
            setTimeout(() => {
                callback();
            }, 100);
        }
        else {
            const onLoad = () => {
                if (callback) {
                    callback();
                }
                if (iframeWindow) {
                    iframeWindow.removeEventListener('load', onLoad);
                }
            };
            iframeWindow.addEventListener('load', onLoad);
        }
    }
    _initEventBubbling() {
        const { animator: animator, _ad: ad, renderer: renderer } = this;
        if (!(animator && ad && renderer)) {
            throw new Error('Failed to init event bubbling. Animator, Ad or Renderer is missing');
        }
        console.debug('[AnimatedCreative] subscribing to animator events');
        animator.on('loop', () => ad.emit(AdEvents.Loop, this._getAnimatorEventData()));
        animator.on('pause', () => ad.emit(AdEvents.Pause, this._getAnimatorEventData()));
        animator.on('play', () => ad.emit(AdEvents.Play, this._getAnimatorEventData()));
        animator.on('stop', () => ad.emit(AdEvents.Stop, this._getAnimatorEventData()));
        animator.on('seek', () => ad.emit(AdEvents.Seek, this._getAnimatorEventData()));
        renderer.on('mute', (muted) => ad.emit(AdEvents.Mute, muted));
        _performanceMark('ac:animated-events:added');
    }
    _getAnimatorEventData() {
        const animator = this.animator;
        return {
            ad: this._ad,
            time: (animator === null || animator === void 0 ? void 0 : animator.time) || 0,
            loop: (animator === null || animator === void 0 ? void 0 : animator.loop) || 0
        };
    }
    _registerAdApi() {
        console.debug('[AnimatedCreative] patching ad api');
        if (!this.animator || !this.renderer) {
            return;
        }
        this._ad.adTag.adApi = new AnimatedCreativeAdApi({
            animatedCreative: this,
            creativeData: this._creativeData,
            creativeDataNode: this._creativeDataNode
        });
    }
    destroy() {
        var _a, _b, _c;
        clearInterval(this._pixelVisibleInterval);
        (_a = this._rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this._onClick);
        this._destroyed = true;
        try {
            if (this._creativeStylesRef) {
                this._document.head.removeChild(this._creativeStylesRef);
                this._creativeStylesRef = undefined;
            }
            // eslint-disable-next-line no-empty
        }
        catch (_d) { }
        (_b = this.renderer) === null || _b === void 0 ? void 0 : _b.destroy();
        (_c = this.animator) === null || _c === void 0 ? void 0 : _c.destroy();
        clearDiContainers();
    }
    /**
     * Preloads all feeds as well as fonts that are used in conjunction as character styling
     */
    async _preloadFeeds() {
        _performanceMark('ac:preload:feeds:start');
        if (!this.feedStore) {
            return;
        }
        this._creativeDataNode.elements.forEach(child => {
            const isElementWithFeedAtRoot = isImageNode(child) || isVideoNode(child);
            if (isTextNode(child)) {
                for (const span of child.content.spans) {
                    if (isVariableSpan(span)) {
                        this._preloadedFeeds.add(span.style.variable.id);
                    }
                }
            }
            else if (child.feed && isElementWithFeedAtRoot) {
                this._preloadedFeeds.add(child.feed.id);
            }
            else if (this._ad.overriddenFeedData && isWidgetNode(child)) {
                child.customProperties
                    .filter(property => property.unit === 'feed')
                    .forEach(feedProperty => {
                    try {
                        const feed = feedProperty.value;
                        if (!isFeedValue(feed) || feed.id.length === 0) {
                            return;
                        }
                        this._preloadedFeeds.add(feed.id);
                    }
                    catch (_a) {
                        /* empty */
                    }
                });
            }
        });
        // Override the first feed, used for example with DCO
        // DCO is only allowed to have 1 feed
        if (this._ad.overriddenFeedData && this._preloadedFeeds.size > 0) {
            this.feedStore.feeds.set([...this._preloadedFeeds][0], {
                feed: {
                    data: this._ad.overriddenFeedData,
                    intervalInSeconds: 0
                },
                interval: undefined,
                overridden: true
            });
        }
        await this.feedStore.preloadFeeds([...this._preloadedFeeds]);
        _performanceMark('ac:preload:feeds:end');
    }
    async _preloadFonts() {
        _performanceMark('ac:preload:fonts:start');
        if (this.feedStore) {
            for (const element of this.renderer.creativeDocument.elements) {
                if (isTextNode(element)) {
                    for (const span of element.content.spans) {
                        if (isVariableSpan(span)) {
                            this.feedStore.addFeedElement(element.id, span.style.variable, element);
                        }
                    }
                }
            }
            for (const id of this._preloadedFeeds) {
                // Adds to fontCharacterCache and uses preload below if in output
                this.feedStore.loadFontsBasedOnFeedData(id, true);
            }
        }
        for (const { value: font } of this._getPreloadFonts()) {
            if (font) {
                await this._preloadFont(font);
            }
        }
        _performanceMark('ac:preload:fonts:end');
    }
    async _preloadFont(font, newContent = '') {
        var _a;
        if (!font.src.startsWith('data')) {
            const [url, encodedChars] = font.src.split(FONT_SERVICE_TEXT_PARAMETER);
            const feedCharacters = ((_a = this.feedStore) === null || _a === void 0 ? void 0 : _a.fontCharacterCache.get(font.id)) || '';
            const textCharacters = decodeURIComponent(encodedChars);
            const { textParams, distinctText } = getFontServiceParamsFromString(feedCharacters + textCharacters + newContent);
            if (feedCharacters && this.feedStore) {
                this.feedStore.fontCharacterCache.set(font.id, distinctText);
            }
            if (newContent || feedCharacters) {
                font.src = url + textParams;
            }
            // Don't load the font if no characters are included
            if (!distinctText) {
                return;
            }
        }
        try {
            const fallbackFontInjection = isBannerflow(window.location.href) && this._ad.parameters['font-loading'];
            if (!fallbackFontInjection) {
                await FontLoader.loadFontFace(font, this._env.FONTSERVICE_API_ORIGIN);
            }
            else {
                injectFontStyle(this._document, font, this._env.FONTSERVICE_API_ORIGIN);
                await this._document.fonts.load(`1em f-${font.id}`); // resolves once the passed font family is loaded
            }
        }
        catch (err) {
            /* Do nothing */
            this._handleError('Could not load font', {
                font,
                originalError: err
            });
        }
    }
    _handleError(message, data) {
        if (isBannerflow(window.location.href)) {
            this._ad.emit(AdEvents.Error, { event: message, data });
        }
    }
    _prepareData(serializedCreativeData) {
        console.debug('[AnimatedCreative] parsing preload assets');
        mapAssets(serializedCreativeData.elements, this._preloadAssets);
        return getParsedCreativeData(serializedCreativeData);
    }
}
